/**
 * PlusHubPage - ASN Plus subscriber dashboard
 * Shows inbox, newsletters, polls, pick'em - all behind Plus paywall.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown, MessageSquare, Mail, Vote, Trophy, Send, Loader2, ArrowLeft,
  Check, ChevronRight, Lock, Clock, BarChart3, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem('userToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============================================
// INBOX TAB
// ============================================
const InboxTab = () => {
  const [convo, setConvo] = useState(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEnd = useRef(null);

  const fetchConvo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plus/inbox/conversations/`, { headers: getHeaders() });
      setConvo(res.data);
      // Mark read
      await axios.post(`${API}/plus/inbox/mark-read/`, {}, { headers: getHeaders() }).catch(() => {});
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConvo(); }, [fetchConvo]);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [convo]);

  const sendMsg = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/plus/inbox/send/`, { content: message, subject }, { headers: getHeaders() });
      setMessage(''); setSubject('');
      fetchConvo();
    } catch {} finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="user-inbox">
      <div className="bg-zinc-900/50 border border-white/5 rounded-lg flex flex-col h-[400px]">
        <div className="p-3 border-b border-white/5">
          <h3 className="text-white text-sm font-bold flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-orange-400" /> Direct Line to ASN
          </h3>
          <p className="text-zinc-600 text-[10px] mt-0.5">Your messages are private and read directly by the ASN team.</p>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {(!convo?.messages || convo.messages.length === 0) ? (
            <div className="text-center py-8">
              <MessageSquare className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-600 text-xs">No messages yet. Send one to start a conversation!</p>
            </div>
          ) : convo.messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] px-4 py-2.5 rounded-lg text-sm ${
                msg.sender === 'user' ? 'bg-orange-500/20 text-orange-100' : 'bg-zinc-800 text-zinc-200'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase text-zinc-500">{msg.sender === 'admin' ? 'ASN Team' : 'You'}</span>
                </div>
                {msg.subject && <p className="font-bold text-xs mb-1">{msg.subject}</p>}
                <p className="text-xs leading-relaxed">{msg.content}</p>
                <p className="text-[9px] text-zinc-600 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
              </div>
            </div>
          ))}
          <div ref={messagesEnd} />
        </div>
        <div className="p-3 border-t border-white/5 space-y-2">
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject (optional)"
            className="w-full bg-zinc-800 border border-white/10 text-white text-xs px-3 py-1.5 rounded" />
          <div className="flex gap-2">
            <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Type your message..."
              className="flex-1 bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded"
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMsg()} data-testid="inbox-message-input" />
            <Button onClick={sendMsg} disabled={sending || !message.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="inbox-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// NEWSLETTERS TAB
// ============================================
const NewslettersTab = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plus/newsletters/`, { headers: getHeaders() });
      setNewsletters(res.data.newsletters || []);
      setUnread(res.data.unread_count || 0);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markRead = async (id) => {
    await axios.post(`${API}/plus/newsletters/${id}/mark-read/`, {}, { headers: getHeaders() }).catch(() => {});
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  if (selected) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    return (
      <div className="space-y-4" data-testid="newsletter-view">
        <button onClick={() => { setSelected(null); fetchData(); }} className="flex items-center gap-2 text-zinc-500 hover:text-white text-xs transition-colors">
          <ArrowLeft className="w-3 h-3" /> Back to newsletters
        </button>
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-6">
          <h2 className="text-white text-xl font-bold mb-1">{selected.title}</h2>
          <p className="text-zinc-600 text-xs mb-4">{new Date(selected.sent_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{selected.content}</div>
          {(selected.attachments || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
              <p className="text-zinc-500 text-[10px] uppercase font-medium">Attachments</p>
              {selected.attachments.map((att, i) => (
                <a key={att.id || i} href={`${BACKEND}${att.url}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded hover:bg-zinc-800 transition-colors text-xs text-orange-400 hover:text-orange-300"
                  data-testid={`attachment-${i}`}>
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                  <span className="flex-1 truncate">{att.original_name}</span>
                  <span className="text-zinc-600">{att.size_kb} KB</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="user-newsletters">
      {unread > 0 && <p className="text-orange-400 text-xs">{unread} unread newsletter{unread !== 1 ? 's' : ''}</p>}
      {newsletters.length === 0 ? (
        <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
          <Mail className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No newsletters yet</p>
          <p className="text-zinc-600 text-xs mt-1">Check back soon for exclusive content from ASN.</p>
        </div>
      ) : newsletters.map(nl => (
        <button key={nl.id} onClick={() => { setSelected(nl); markRead(nl.id); }}
          className="w-full text-left flex items-center gap-4 p-4 bg-zinc-900/50 border border-white/5 hover:border-orange-500/20 rounded-lg transition-colors group">
          <Mail className={`w-5 h-5 flex-shrink-0 ${nl.is_read ? 'text-zinc-600' : 'text-orange-400'}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${nl.is_read ? 'text-zinc-400' : 'text-white'}`}>{nl.title}</span>
              {!nl.is_read && <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />}
            </div>
            {nl.summary && <p className="text-zinc-500 text-xs mt-0.5 truncate">{nl.summary}</p>}
            <p className="text-zinc-600 text-[10px] mt-0.5">{new Date(nl.sent_at).toLocaleDateString()}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white transition-colors flex-shrink-0" />
        </button>
      ))}
    </div>
  );
};

// ============================================
// POLLS TAB
// ============================================
const PollsTab = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPolls = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plus/polls/`, { headers: getHeaders() });
      setPolls(res.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPolls(); }, [fetchPolls]);

  const vote = async (pollId, optionId) => {
    try {
      await axios.post(`${API}/plus/polls/${pollId}/vote?option_id=${optionId}`, {}, { headers: getHeaders() });
      fetchPolls();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to vote');
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="user-polls">
      {polls.length === 0 ? (
        <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
          <Vote className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No polls available yet</p>
        </div>
      ) : polls.map(poll => (
        <div key={poll.id} className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-bold">{poll.question}</h3>
            <span className={`px-1.5 py-0.5 text-[10px] rounded ${poll.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
              {poll.status}
            </span>
          </div>
          {poll.description && <p className="text-zinc-500 text-xs mb-3">{poll.description}</p>}
          <div className="space-y-2">
            {poll.options.map(opt => {
              const pct = poll.total_votes > 0 ? Math.round(opt.votes / poll.total_votes * 100) : 0;
              const showResults = poll.has_voted || poll.status === 'closed';
              return (
                <div key={opt.id} className="relative">
                  {!poll.has_voted && poll.status === 'active' ? (
                    <button onClick={() => vote(poll.id, opt.id)}
                      className="w-full text-left px-4 py-2.5 text-xs text-zinc-300 border border-white/10 rounded hover:border-orange-500/50 hover:bg-orange-500/5 transition-colors"
                      data-testid={`vote-${poll.id}-${opt.id}`}>
                      {opt.text}
                    </button>
                  ) : (
                    <div className="relative overflow-hidden rounded">
                      <div className="flex items-center justify-between text-xs py-2.5 px-4 relative z-10">
                        <span className="text-zinc-300">{opt.text}</span>
                        <span className="text-zinc-500 font-medium">{pct}%</span>
                      </div>
                      <div className="absolute inset-0 bg-orange-500/10 transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {(poll.has_voted || poll.status === 'closed') && (
            <p className="text-zinc-600 text-[10px] mt-2">{poll.total_votes} total vote{poll.total_votes !== 1 ? 's' : ''}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================
// PICK'EM TAB
// ============================================
const PickemTab = () => {
  const [weeks, setWeeks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState('weeks');
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  const fetchData = useCallback(async () => {
    try {
      const [wRes, lRes] = await Promise.all([
        axios.get(`${API}/plus/pickem/weeks/`, { headers: getHeaders() }),
        axios.get(`${API}/plus/pickem/leaderboard/`, { headers: getHeaders() })
      ]);
      setWeeks(wRes.data);
      setLeaderboard(lRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitPicks = async (weekId) => {
    const weekPicks = picks[weekId];
    if (!weekPicks || Object.keys(weekPicks).length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/plus/pickem/weeks/${weekId}/pick`, { picks: weekPicks }, { headers: getHeaders() });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const setPick = (weekId, gameId, pick) => {
    setPicks(prev => ({
      ...prev,
      [weekId]: { ...(prev[weekId] || {}), [gameId]: pick }
    }));
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="user-pickem">
      <div className="flex gap-2">
        <button onClick={() => setView('weeks')}
          className={`px-3 py-1.5 text-xs rounded border transition-colors ${view === 'weeks' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400'}`}>
          This Week
        </button>
        <button onClick={() => setView('leaderboard')}
          className={`px-3 py-1.5 text-xs rounded border transition-colors ${view === 'leaderboard' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400'}`}
          data-testid="leaderboard-btn">
          Leaderboard
        </button>
      </div>

      {view === 'leaderboard' && (
        <div className="bg-zinc-900/50 border border-white/5 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-white text-sm font-bold flex items-center gap-2"><Trophy className="w-4 h-4 text-orange-400" /> All-Time Leaderboard</h3>
          </div>
          {leaderboard.length === 0 ? (
            <p className="p-4 text-zinc-600 text-xs text-center">No graded weeks yet</p>
          ) : (
            <div className="divide-y divide-white/5">
              {leaderboard.map((entry, i) => (
                <div key={entry.user_id} className={`flex items-center gap-4 px-4 py-3 ${entry.user_id === userData?.id ? 'bg-orange-500/5' : ''}`}>
                  <span className={`w-7 text-center font-bold text-sm ${i < 3 ? 'text-orange-400' : 'text-zinc-600'}`}>{i + 1}</span>
                  <span className="flex-1 text-white text-sm">{entry.user_name || 'Anonymous'}</span>
                  <span className="text-orange-400 font-bold text-sm">{entry.total_correct}</span>
                  <span className="text-zinc-600 text-xs">/ {entry.total_games}</span>
                  <span className="text-zinc-500 text-xs w-12 text-right">{entry.accuracy}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'weeks' && (
        <>
          {weeks.length === 0 ? (
            <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
              <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No Pick'em contests available yet</p>
              <p className="text-zinc-600 text-xs mt-1">Check back when games are scheduled!</p>
            </div>
          ) : weeks.map(week => {
            const hasPicked = !!week.user_pick;
            const isActive = week.status === 'active';
            const isGraded = week.status === 'graded';
            const weekPicks = picks[week.id] || {};

            return (
              <div key={week.id} className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-bold text-sm">{week.title}</h3>
                  <div className="flex items-center gap-2">
                    {hasPicked && <span className="text-green-400 text-[10px] flex items-center gap-1"><Check className="w-3 h-3" /> Submitted</span>}
                    {isGraded && week.user_pick && (
                      <span className="text-orange-400 text-xs font-bold">{week.user_pick.correct}/{week.user_pick.total} correct</span>
                    )}
                    <span className={`px-1.5 py-0.5 text-[10px] rounded ${isActive ? 'bg-green-500/20 text-green-400' : isGraded ? 'bg-orange-500/20 text-orange-400' : 'bg-zinc-700 text-zinc-400'}`}>
                      {week.status}
                    </span>
                  </div>
                </div>
                {isActive && <p className="text-zinc-600 text-[10px] mb-3 flex items-center gap-1"><Clock className="w-3 h-3" /> Deadline: {new Date(week.deadline).toLocaleString()}</p>}

                <div className="space-y-2">
                  {(week.games || []).map(game => {
                    const userPick = hasPicked ? week.user_pick.picks[game.id] : weekPicks[game.id];
                    const result = week.results?.[game.id];
                    return (
                      <div key={game.id} className="flex items-center gap-3 p-3 bg-zinc-800/50 border border-white/5 rounded">
                        <div className="flex-1 flex items-center gap-3">
                          <button
                            onClick={() => !hasPicked && isActive && setPick(week.id, game.id, 'home')}
                            disabled={hasPicked || !isActive}
                            className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                              userPick === 'home'
                                ? result === 'home' ? 'bg-green-500/20 border-green-500/30 text-green-400'
                                : result === 'away' ? 'bg-red-500/20 border-red-500/30 text-red-400'
                                : 'bg-orange-500/20 border-orange-500/30 text-orange-400'
                                : 'bg-zinc-800 border-white/10 text-zinc-400 hover:border-white/20'
                            }`}>
                            {game.home_team?.abbreviation || 'HOME'}
                          </button>
                          <span className="text-zinc-600 text-[10px]">vs</span>
                          <button
                            onClick={() => !hasPicked && isActive && setPick(week.id, game.id, 'away')}
                            disabled={hasPicked || !isActive}
                            className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                              userPick === 'away'
                                ? result === 'away' ? 'bg-green-500/20 border-green-500/30 text-green-400'
                                : result === 'home' ? 'bg-red-500/20 border-red-500/30 text-red-400'
                                : 'bg-orange-500/20 border-orange-500/30 text-orange-400'
                                : 'bg-zinc-800 border-white/10 text-zinc-400 hover:border-white/20'
                            }`}>
                            {game.away_team?.abbreviation || 'AWAY'}
                          </button>
                        </div>
                        <span className="text-zinc-600 text-[10px]">{game.scheduled_date}</span>
                      </div>
                    );
                  })}
                </div>

                {isActive && !hasPicked && (
                  <Button onClick={() => submitPicks(week.id)} disabled={submitting || Object.keys(weekPicks).length === 0}
                    className="mt-3 bg-orange-500 hover:bg-orange-600 text-xs" data-testid={`submit-picks-${week.id}`}>
                    {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                    Submit Picks ({Object.keys(weekPicks).length}/{(week.games || []).length})
                  </Button>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

// ============================================
// MAIN HUB PAGE
// ============================================
export const PlusHubPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inbox');
  const userToken = localStorage.getItem('userToken');
  const userData = JSON.parse(localStorage.getItem('userData') || 'null');
  const isPlus = userData?.is_plus === true;

  if (!userToken) {
    return (
      <main className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Sign in required</h2>
          <Button onClick={() => navigate('/login-user?return=/plus/hub')} className="bg-orange-500 hover:bg-orange-600">Sign In</Button>
        </div>
      </main>
    );
  }

  if (!isPlus) {
    return (
      <main className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <img src="/asn-plus-logo.png" alt="ASN Plus" className="w-20 h-20 mx-auto mb-4 object-contain" />
          <h2 className="text-white text-xl font-bold mb-2">ASN Plus Required</h2>
          <p className="text-zinc-500 text-sm mb-4">Subscribe to ASN Plus to access the exclusive inbox, newsletters, polls, and Pick'em contests.</p>
          <Button onClick={() => navigate('/plus')} className="bg-orange-500 hover:bg-orange-600" data-testid="upgrade-btn">
            <Crown className="w-4 h-4 mr-2" /> Get ASN Plus - $3.99/mo
          </Button>
        </div>
      </main>
    );
  }

  const tabs = [
    { id: 'inbox', label: 'Inbox', icon: MessageSquare },
    { id: 'newsletters', label: 'Newsletters', icon: Mail },
    { id: 'polls', label: 'Polls', icon: Vote },
    { id: 'pickem', label: "Pick'em", icon: Trophy }
  ];

  return (
    <main className="min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-4 mb-8">
          <img src="/asn-plus-logo.png" alt="ASN Plus" className="w-10 h-10 object-contain" />
          <div>
            <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white" data-testid="plus-hub-title">ASN Plus Hub</h1>
            <p className="text-zinc-500 text-xs">Your exclusive subscriber features</p>
          </div>
        </div>

        <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto scrollbar-hide">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                activeTab === t.id ? 'text-white border-orange-500' : 'text-zinc-500 border-transparent hover:text-zinc-300'
              }`} data-testid={`hub-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'inbox' && <InboxTab />}
        {activeTab === 'newsletters' && <NewslettersTab />}
        {activeTab === 'polls' && <PollsTab />}
        {activeTab === 'pickem' && <PickemTab />}
      </div>
    </main>
  );
};

export default PlusHubPage;
